<template>
	<div class="ddd-panels">
		<!-- Score panel -->
		<div v-if="props.score" class="ddd-panel">
			<div class="ddd-panel-header">
				<v-icon size="14" class="mr-1">mdi-chart-bar</v-icon>
				{{ t('planning_driver_debug_score') }}
			</div>
			<div v-for="(val, key) in props.score" :key="'ps-' + key" class="ddd-panel-row">
				<span class="ddd-panel-key">{{ key }}</span>
				<span class="ddd-panel-val">{{ Math.round(val * 1000) / 1000 }}</span>
			</div>
		</div>

		<!-- Objectives panel -->
		<div v-if="props.objectives" class="ddd-panel">
			<div class="ddd-panel-header">
				<v-icon size="14" class="mr-1">mdi-target</v-icon>
				{{ t('core_objectives') }}
			</div>
			<div class="ddd-panel-row">
				<span class="ddd-panel-key">{{ t('core_chart_violations') }}</span>
				<span class="ddd-panel-val ddd-panel-val--error">{{ props.violations }}</span>
			</div>
			<div v-for="(val, key) in props.objectives" :key="'po-' + key" class="ddd-panel-row">
				<span class="ddd-panel-key">{{ t('core_objectives_' + key) }}</span>
				<span class="ddd-panel-val">{{ formatToTwoDecimals(val) }}</span>
			</div>
		</div>

		<!-- Driver annotation panel -->
		<div v-if="props.driverAnnotation" class="ddd-panel">
			<div class="ddd-panel-header">
				<v-icon size="14" class="mr-1">mdi-database</v-icon>
				{{ t('planning_driver_debug_fitnessData') }} (Driver)
			</div>
			<div class="ddd-panel-row">
				<span class="ddd-panel-key">pensum</span>
				<span class="ddd-panel-val">{{ props.driverAnnotation.pensum }}</span>
			</div>
			<div class="ddd-panel-row">
				<span class="ddd-panel-key">workTimeDifference</span>
				<span class="ddd-panel-val">{{ formatToTwoDecimals(props.driverAnnotation.workTimeDifference) }}</span>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { formatToTwoDecimals } from '@/rennovation/shared/utils/date-utils';

// ─────────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────────
const props = defineProps<{
	score?: Record<string, number>;
	objectives?: Record<string, number>;
	violations?: number;
	driverAnnotation?: { pensum: number; workTimeDifference?: number } | null;
}>();

// ─────────────────────────────────────────────────────────────────────────────
// i18n
// ─────────────────────────────────────────────────────────────────────────────
const { t } = useI18n();
</script>

<style scoped>
/* ── Summary panels ──────────────────────────────────────────────────────── */
.ddd-panels {
	display: flex;
	flex-wrap: wrap;
	gap: 16px;
	padding: 20px 18px;
	position: sticky;
	left: 0;
	align-items: flex-start;
	max-width: 600px;
}

.ddd-panel {
	min-width: 280px;
	max-width: 480px;
	border: 1px solid var(--ddd-border);
	border-radius: 8px;
	overflow: hidden;
	flex: 1;
}

.ddd-panel-header {
	padding: 8px 14px;
	background: var(--ddd-bg-panel-header);
	border-bottom: 1px solid var(--ddd-border);
	font-family: var(--ddd-font-ui);
	font-size: 11px;
	font-weight: 700;
	color: var(--ddd-text-primary);
	text-transform: uppercase;
	letter-spacing: 0.07em;
	display: flex;
	align-items: center;
}

.ddd-panel-row {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 5px 14px;
	border-bottom: 1px solid var(--ddd-bg-panel-header);
	font-size: 12.5px;
}

.ddd-panel-row:last-child {
	border-bottom: none;
}

.ddd-panel-key {
	font-family: var(--ddd-font-ui);
	color: var(--ddd-text-dark);
	flex: 1;
}

.ddd-panel-val {
	font-family: var(--ddd-font-mono);
	font-weight: 700;
	color: var(--ddd-text-primary);
	min-width: 64px;
	text-align: right;
	font-size: 12px;
}

.ddd-panel-val--error {
	color: var(--ddd-color-viol);
}
</style>
